import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import * as React from "react";
import { useAsyncFn, useMount } from "react-use";
import { Loader } from "src/components/loader/loader";
import PaymentMethodsItem from "src/components/payment-method-item/payment-method-item";
import useSharedMerchant from "src/hooks/use-merchant";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import merchantProvider from "src/providers/merchant-provider";
import type { BlockchainTicker } from "src/types/index";

interface AvailableBlockchainsType {
	name: string;
}

const PaymentMethodsSelect: React.FC = () => {
	const { merchant, getMerchant } = useSharedMerchant();
	const { merchantId } = useActiveMerchantId();
	const [supportedMethodsReqState, updateSupportedMethods] = useAsyncFn(
		merchantProvider.updateSupportedMethods,
	);
	const [availableBlockchains, setAvailableBlockchains] = React.useState<
		AvailableBlockchainsType[]
	>([]);
	const [isAvailableBlockchainsLoading, setIsAvailableBlockchainsLoading] =
		React.useState<boolean>(false);

	const onChange = (ticker: BlockchainTicker) => {
		if (!merchantId || !merchant?.supportedPaymentMethods) {
			return;
		}

		const newPaymentMethods = [...merchant.supportedPaymentMethods];
		const index = newPaymentMethods.findIndex((item) => item.ticker === ticker);
		newPaymentMethods[index].enabled = !newPaymentMethods[index].enabled;

		const supportedPaymentMethods = newPaymentMethods
			.filter((item) => item.enabled)
			.map((item) => item.ticker);

		updateSupportedMethods(merchantId, { supportedPaymentMethods });
	};

	const getBlockchainsList = () => {
		if (!merchant) {
			return [];
		}

		return [
			...new Set(
				merchant.supportedPaymentMethods.map((item) => item.blockchainName),
			),
		].map((item) => ({
			name: item,
		}));
	};

	const updateMerchant = async () => {
		if (!merchantId) {
			return;
		}

		await getMerchant(merchantId);
	};

	useMount(async () => {
		await updateMerchant();
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: updateMerchant is stable
	React.useEffect(() => {
		updateMerchant();
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getBlockchainsList is stable
	React.useEffect(() => {
		setIsAvailableBlockchainsLoading(true);
		setAvailableBlockchains(getBlockchainsList());
		setIsAvailableBlockchainsLoading(false);
	}, []);

	return (
		<div className="space-y-4">
			<h3 className="text-2xl font-semibold">Accepted Currencies</h3>

			{isAvailableBlockchainsLoading ? (
				<Loader size="md" fullScreen />
			) : availableBlockchains.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 px-8 text-center border rounded-lg bg-muted/50">
					<div className="space-y-4 max-w-md">
						<div className="space-y-2">
							<h3 className="text-lg font-semibold">
								No currencies configured
							</h3>
							<p className="text-sm text-muted-foreground">
								Accepted currencies will appear here after you add new types of
								supported currency
							</p>
						</div>
					</div>
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[200px]">Network</TableHead>
							<TableHead>Currencies</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{availableBlockchains.map((record) => (
							<TableRow key={record.name}>
								<TableCell className="font-medium whitespace-nowrap">
									{record.name}
								</TableCell>
								<TableCell>
									<PaymentMethodsItem
										title={record.name}
										items={
											merchant?.supportedPaymentMethods.filter(
												(paymentItem) =>
													paymentItem.blockchainName === record.name,
											) ?? []
										}
										onChange={onChange}
										isLoading={supportedMethodsReqState.loading}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</div>
	);
};

export default PaymentMethodsSelect;
